import React, { useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Switch,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Key as KeyIcon,
  Upgrade as UpgradeIcon,
} from '@mui/icons-material';

const Settings: React.FC = () => {
  const [openPlanDialog, setOpenPlanDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('premium');

  const plans = [
    {
      name: 'Basic',
      price: '$49/month',
      features: [
        'Essential online presence management',
        'Listing synchronization',
        'Basic analytics',
        'Email support',
      ],
    },
    {
      name: 'Premium',
      price: '$199/month',
      features: [
        'Everything in Basic',
        'Social media scheduling',
        'Automated content generation',
        'Advanced local SEO',
        'Priority support',
      ],
    },
    {
      name: 'Enterprise',
      price: '$499/month',
      features: [
        'Everything in Premium',
        'Dedicated account manager',
        'Custom integrations',
        'Advanced analytics',
        'API access',
        '24/7 phone support',
      ],
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>

      <Grid container spacing={3}>
        {/* Account Settings */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Account Settings
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  defaultValue="user@example.com"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  defaultValue="John Smith"
                />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained">
                  Update Account
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* API Credentials */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              API Credentials
            </Typography>
            <List>
              {[
                { name: 'Google API Key', value: '**********************abcd' },
                { name: 'Facebook Access Token', value: '**********************efgh' },
                { name: 'Twitter API Key', value: '**********************ijkl' },
                { name: 'Yelp API Key', value: '**********************mnop' },
              ].map((credential) => (
                <ListItem key={credential.name}>
                  <ListItemText
                    primary={credential.name}
                    secondary={credential.value}
                  />
                  <ListItemSecondaryAction>
                    <Button startIcon={<KeyIcon />}>
                      Update
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>

          {/* Notification Settings */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Notification Settings
            </Typography>
            <List>
              {[
                { name: 'Email Notifications', description: 'Receive updates via email' },
                { name: 'Push Notifications', description: 'Receive push notifications' },
                { name: 'Review Alerts', description: 'Get notified about new reviews' },
                { name: 'Analytics Reports', description: 'Weekly performance reports' },
              ].map((setting) => (
                <ListItem key={setting.name}>
                  <ListItemText
                    primary={setting.name}
                    secondary={setting.description}
                  />
                  <ListItemSecondaryAction>
                    <Switch defaultChecked />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Current Plan */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Current Plan
            </Typography>
            <Box mb={2}>
              <Typography variant="h4" color="primary" gutterBottom>
                Premium
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                $199/month
              </Typography>
              <Button
                variant="outlined"
                startIcon={<UpgradeIcon />}
                fullWidth
                onClick={() => setOpenPlanDialog(true)}
                sx={{ mt: 2 }}
              >
                Change Plan
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Next billing date: Jan 1, 2024
            </Typography>
            <Alert severity="info" sx={{ mt: 2 }}>
              Your plan will automatically renew on the billing date.
            </Alert>
          </Paper>

          {/* Security Settings */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Security
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Two-Factor Authentication"
                  secondary="Add an extra layer of security"
                />
                <ListItemSecondaryAction>
                  <Switch />
                </ListItemSecondaryAction>
              </ListItem>
              <ListItem>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                >
                  Change Password
                </Button>
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Change Plan Dialog */}
      <Dialog
        open={openPlanDialog}
        onClose={() => setOpenPlanDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Choose a Plan</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {plans.map((plan) => (
              <Grid item xs={12} md={4} key={plan.name}>
                <Card
                  variant={selectedPlan === plan.name.toLowerCase() ? 'elevation' : 'outlined'}
                  sx={{
                    height: '100%',
                    cursor: 'pointer',
                    bgcolor: selectedPlan === plan.name.toLowerCase() ? 'primary.light' : 'background.paper',
                  }}
                  onClick={() => setSelectedPlan(plan.name.toLowerCase())}
                >
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      {plan.name}
                    </Typography>
                    <Typography variant="h4" color="primary" gutterBottom>
                      {plan.price}
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <List dense>
                      {plan.features.map((feature) => (
                        <ListItem key={feature}>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPlanDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => setOpenPlanDialog(false)}
          >
            Confirm Change
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;
